import { addToast } from '@heroui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  isTestCaseEditorValid,
  toCollectExpectedResults,
  type TestCaseEditorValues,
  type TestCaseSelection
} from '../components/CollectExecutionDrawer';

import { isDatasetLockError } from '~/api/agentLogs';
import type { AgentExecutionDetail } from '~/api/agentLogs/types';
import type { DatasetEval } from '~/api/experiments/types';
import i18n from '~/common/helpers/i18n';
import { useAgentDatasetEvals } from '~/scenes/Monitor/scenes/AgentLogs/hooks/useAgentDatasetEvals';
import { useAgentDatasetExperiments } from '~/scenes/Monitor/scenes/AgentLogs/hooks/useAgentDatasetExperiments';
import { useAgentDatasetsForCollect } from '~/scenes/Monitor/scenes/AgentLogs/hooks/useAgentDatasetsForCollect';
import { useCollectAgentExecution } from '~/scenes/Monitor/scenes/AgentLogs/hooks/useCollectAgentExecution';
import { mapExecutionToTestCaseVariables } from '~/scenes/Monitor/scenes/AgentLogs/utils/mapExecutionToTestCaseVariables';
import type { CollectTestCaseExpectedResultItem } from '~/scenes/Monitor/scenes/AgentLogs/utils/normalizeDatasetExperiments';

function buildDatasetVariableKeys(
  testCases: Array<{ variables: Record<string, string> }>
): string[] {
  return Array.from(
    new Set(testCases.flatMap(testCase => Object.keys(testCase.variables)))
  );
}

function stringifyExpectedResult(
  expectedResult: Record<string, unknown>
): string {
  if (Object.keys(expectedResult).length === 0) {
    return '';
  }

  return JSON.stringify(expectedResult, null, 2);
}

function buildExpectedResultsMap(
  datasetEvals: DatasetEval[],
  existing?: CollectTestCaseExpectedResultItem[]
): Record<string, string> {
  const existingByEvalId = new Map(
    (existing ?? []).map(item => [item.evalId, item.expectedResult])
  );

  return Object.fromEntries(
    datasetEvals.map(datasetEval => {
      const existingValue = existingByEvalId.get(datasetEval.evalId);
      if (existingValue) {
        return [datasetEval.evalId, stringifyExpectedResult(existingValue)];
      }

      return [datasetEval.evalId, ''];
    })
  );
}

function emptyEditorValues(): TestCaseEditorValues {
  return {
    name: '',
    variables: {},
    expectedResults: {}
  };
}

function isHttpConflictError(error: unknown): boolean {
  if (isDatasetLockError(error)) {
    return true;
  }

  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const { response } = error as { response?: { status?: number } };
  return response?.status === 409;
}

type UseAgentLogCollectPanelStateParams = {
  realm: string;
  detail: AgentExecutionDetail;
};

export function useAgentLogCollectPanelState({
  realm,
  detail
}: UseAgentLogCollectPanelStateParams) {
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(
    null
  );
  const [selection, setSelection] = useState<TestCaseSelection>({
    type: 'new'
  });
  const [editorValues, setEditorValues] =
    useState<TestCaseEditorValues>(emptyEditorValues);
  const [editorSeed, setEditorSeed] = useState(0);
  const [isEditorValid, setIsEditorValid] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const { data: datasets = [], isLoading: datasetsLoading } =
    useAgentDatasetsForCollect({
      realm,
      pipelineName: detail.pipelineName,
      pipelineVersion: detail.pipelineVersion,
      connectorId: detail.connectorId,
      enabled: true
    });

  const { testCases, isLoading: testCasesLoading } = useAgentDatasetExperiments(
    {
      datasets,
      selectedDatasetId,
      isDatasetsLoading: datasetsLoading
    }
  );

  const { data: datasetEvals = [], isLoading: evalsLoading } =
    useAgentDatasetEvals({
      realm,
      pipelineName: detail.pipelineName,
      pipelineVersion: detail.pipelineVersion,
      connectorId: detail.connectorId,
      datasetId: selectedDatasetId
    });

  const { mutateAsync: saveTestCase, isPending } = useCollectAgentExecution({
    realm,
    detail,
    datasetId: selectedDatasetId
  });

  const datasetEvalIds = useMemo(
    () => datasetEvals.map(item => item.evalId).join(','),
    [datasetEvals]
  );

  const existingNames = useMemo(
    () => testCases.map(testCase => testCase.name),
    [testCases]
  );

  useEffect(() => {
    setSelection({ type: 'new' });
  }, [selectedDatasetId]);

  useEffect(() => {
    if (!selectedDatasetId) {
      setEditorValues(emptyEditorValues());
      setEditorSeed(seed => seed + 1);
      setNameError(null);
      setIsEditorValid(false);
      return;
    }

    if (evalsLoading) {
      return;
    }

    if (selection.type === 'existing') {
      const selected = testCases.find(
        testCase => testCase.id === selection.experimentId
      );
      if (!selected) {
        return;
      }

      setEditorValues({
        name: selected.name,
        variables: { ...selected.variables },
        expectedResults: buildExpectedResultsMap(
          datasetEvals,
          selected.expectedResults
        )
      });
      setEditorSeed(seed => seed + 1);
      setNameError(null);
      return;
    }

    const mapped = mapExecutionToTestCaseVariables(detail, {
      datasetVariableKeys: buildDatasetVariableKeys(testCases)
    });
    setEditorValues({
      name: mapped.name,
      variables: mapped.variables,
      expectedResults: buildExpectedResultsMap(datasetEvals)
    });
    setEditorSeed(seed => seed + 1);
    setNameError(null);
    // Re-seed only when dataset/selection/evals change, not while typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDatasetId,
    selection,
    detail.agentExecutionKey,
    evalsLoading,
    datasetEvalIds
  ]);

  const handleEditorChange = useCallback((next: TestCaseEditorValues) => {
    setEditorValues(next);
    setNameError(null);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selectedDatasetId || !isTestCaseEditorValid(editorValues)) {
      setNameError(
        editorValues.name.trim()
          ? null
          : (i18n.t(
              'scenes.build.execution.experiments.save.empty_experiment_name'
            ) as string)
      );
      return;
    }

    const duplicateName = existingNames.some(
      name =>
        name.trim().toLowerCase() === editorValues.name.trim().toLowerCase() &&
        (selection.type === 'new' ||
          testCases.find(testCase => testCase.id === selection.experimentId)
            ?.name !== name)
    );

    if (duplicateName) {
      setNameError(
        i18n.t(
          'scenes.build.execution.experiments.save.duplicate_experiment_name',
          { name: editorValues.name.trim() }
        ) as string
      );
      return;
    }

    const payload = {
      name: editorValues.name.trim(),
      variables: editorValues.variables,
      expectedResults: toCollectExpectedResults(editorValues.expectedResults)
    };

    try {
      if (selection.type === 'existing') {
        await saveTestCase({
          mode: 'update',
          experimentId: selection.experimentId,
          payload
        });
        addToast({
          title: i18n.t('scenes.monitor.agentLogs.collect.updateSuccess'),
          color: 'success',
          variant: 'flat'
        });
        return;
      }

      const created = await saveTestCase({
        mode: 'create',
        payload
      });

      if (created?.id) {
        setSelection({ type: 'existing', experimentId: created.id });
      }

      addToast({
        title: i18n.t('scenes.monitor.agentLogs.collect.createSuccess'),
        color: 'success',
        variant: 'flat'
      });
    } catch (error) {
      if (isHttpConflictError(error)) {
        addToast({
          title: i18n.t('scenes.monitor.agentLogs.errors.datasetLocked'),
          color: 'danger',
          variant: 'flat'
        });
        return;
      }

      addToast({
        title: i18n.t('scenes.monitor.agentLogs.errors.generic'),
        color: 'danger',
        variant: 'flat'
      });
    }
  }, [
    selectedDatasetId,
    editorValues,
    existingNames,
    selection,
    testCases,
    saveTestCase
  ]);

  const showEmptyState = !selectedDatasetId;
  const showLoadingState =
    Boolean(selectedDatasetId) && (testCasesLoading || evalsLoading);
  const showWorkspace =
    Boolean(selectedDatasetId) && !testCasesLoading && !evalsLoading;
  const canSave =
    Boolean(selectedDatasetId) &&
    isEditorValid &&
    isTestCaseEditorValid(editorValues);

  return {
    datasets,
    datasetsLoading,
    selectedDatasetId,
    setSelectedDatasetId,
    selection,
    setSelection,
    testCases,
    datasetEvals,
    editorValues,
    editorSeed,
    nameError,
    handleEditorChange,
    setIsEditorValid,
    handleConfirm,
    isPending,
    canSave,
    showEmptyState,
    showLoadingState,
    showWorkspace
  };
}
